import dayjs, { Dayjs } from 'dayjs';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import Lang from '@/modules/core/app/services/Lang';
import { Duration } from 'dayjs/plugin/duration';
import { DayjsDateRange } from '@/modules/core/app/helpers/interfaces/DayjsDateRange';
import { cloneDeep } from 'lodash';

/**
 * Turns '01-05-2021' into '01/05/2021'
 */
export function convertHyphenISOToForwardSlash(date: string): string {
  return date.replace(/-/g, '/');
}

/**
 * Turns a date into a relative period of time like 2 days ago if less than 30 days ago
 */
export function timeAgo(date: string): string {
  if (dayjs().diff(date, 'days') > 30) {
    return dayjs(date).format(DateFormat.MONTH_DAY_YEAR);
  }
  return dayjs(date).fromNow();
}

export function nextBusinessDay(
  date: dayjs | string | Date,
  format: string,
  noOfDays: number
): Date {
  noOfDays = Math.abs(noOfDays);
  while (noOfDays !== 0) {
    const tomorrow: Dayjs = dayjs(date).add(1, 'days');
    if (tomorrow.day() !== 6 && tomorrow.day() !== 0) {
      noOfDays -= 1;
    }
    date = tomorrow;
  }

  return dayjs(date, format).toDate();
}

export function startDay(date: string): string {
  return dayjs(date).startOf('day').toISOString();
}

export function canonicalStartDay(date: string): string {
  const localDate: string = dayjs(date).format(DateFormat.ISO);
  return dayjs.utc(`${localDate}T00:00:00Z`).toISOString();
}

export function createLocalDateFromUTCString(utcDateString: string): Date {
  const dateOnly: string = utcDateString.substring(0, 10);
  return dayjs(dateOnly).hour(12).toDate();
}

export function endDay(date: string): string {
  return dayjs(date).endOf('day').toISOString();
}

export function canonicalEndDay(date: string): string {
  const localDate: string = dayjs(date).format(DateFormat.ISO);
  return dayjs.utc(`${localDate}T23:59:59Z`).toISOString();
}

export function numberOfDaysFromTodayToDate(date: string): number {
  if (dayjs(date).isBefore(dayjs())) {
    return dayjs().diff(dayjs(date), 'days');
  }

  return dayjs(date).diff(dayjs(), 'days');
}

export function isOnDate(date: string, dateToMatch: string): boolean {
  return dayjs(date)
    .utc()
    .isBetween(dayjs(startDay(dateToMatch)).utc(), dayjs(endDay(dateToMatch)).utc(), '', '[]');
}

export function isOnCanonicalDate(date: string, dateToMatch: string): boolean {
  const dateOnly: string = date?.substring(0, 10);
  const dateToMatchOnly: string = dateToMatch?.substring(0, 10);
  return dateOnly === dateToMatchOnly;
}

export function isToday(date: string): boolean {
  return dayjs(date).isSame(dayjs(), 'day');
}

export function isTodayOrTomorrow(date: string): boolean {
  return (
    dayjs(date).isSame(dayjs().utc(), 'day') ||
    dayjs(date).utc().isSame(dayjs().add(1, 'day'), 'day')
  );
}

export function isInPast(date: string): boolean {
  return dayjs(date).utc().isBefore(dayjs().utc());
}

export function isYesterday(date: string): boolean {
  return dayjs(date).isSame(dayjs().subtract(1, 'day'), 'day');
}

export function isTodayOrBefore(date: string): boolean {
  return dayjs(date).isSameOrBefore(dayjs());
}

export function isInThisWeek(date: string): boolean {
  return dayjs(date).isSame(dayjs(), 'week');
}

export function isInNextWeek(date: string): boolean {
  return dayjs(date).isSame(dayjs().add(1, 'weeks').startOf('isoWeek'), 'week');
}

export function startDateOfPreviousWeek(): Dayjs {
  return dayjs().subtract(1, 'weeks').startOf('isoWeek');
}

export function endDateOfPreviousWeek(): Dayjs {
  return dayjs().subtract(1, 'weeks').endOf('isoWeek');
}

export function startDateOfPreviousMonth(): Dayjs {
  return dayjs().subtract(1, 'months').startOf('month');
}

export function endDateOfPreviousMonth(): Dayjs {
  return dayjs().subtract(1, 'months').endOf('month');
}

export function minuteDurationToTime(numMinutes: number): string {
  if (numMinutes <= 0) {
    return '00:00';
  }

  const minutes: number = numMinutes % 60;
  const hours: number = (numMinutes - minutes) / 60;
  return `${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}`;
}

export function durationBetween(start: string, end: string): Duration {
  return dayjs.duration(dayjs(end).diff(dayjs(start)));
}

export function humanizeDate(
  date: string,
  lowercase: boolean = false,
  prefix: string = ''
): string {
  if (isToday(date)) {
    const dateString: string = Lang.t('date.today');

    return lowercase ? dateString.toLowerCase() : dateString;
  }

  if (isYesterday(date)) {
    const dateString: string = Lang.t('date.yesterday');

    return lowercase ? dateString.toLowerCase() : dateString;
  }

  return `${prefix}${dayjs(date).format(DateFormat.MONTH_DAY_YEAR_WITH_ORDINAL)}`;
}

/**
 * Returns an array containing dates of a week.
 * Any date of the week can be provided as input.
 * The week starts from Sunday when includeWeekend is true, else, starts on Monday
 */
export function datesOfWeek(date: string | Dayjs, includeWeekend: boolean = true): string[] {
  const dayjsDate: Dayjs = dayjs.isDayjs(date) ? date : dayjs(date);
  const weekDates: string[] = [];
  const SUNDAY: number = 0;
  const SATURDAY: number = 6;

  for (let day: number = 0; day < 7; day++) {
    if (!includeWeekend && [SUNDAY, SATURDAY].includes(day)) {
      continue;
    }
    weekDates.push(dayjs(dayjsDate).weekday(day).format(DateFormat.ISO));
  }

  return weekDates;
}

export function fullMonthDayYearDateFormat(date: string): string {
  return dayjs(date).format(DateFormat.FULL_MONTH_DAY_YEAR);
}

export function yearsAgo(years: number): Date {
  return dayjs().subtract(years, 'years').toDate();
}

export function hasOverlap(
  startDate?: string,
  endDate?: string,
  otherStartDate?: string,
  otherEndDate?: string
): boolean {
  if (startDate && endDate) {
    return dayjs(startDate).isBefore(otherEndDate) && dayjs(endDate).isAfter(otherStartDate);
  }

  return (
    !!(startDate && dayjs(startDate).isBetween(otherStartDate!, otherEndDate!)) ||
    !!(endDate && dayjs(endDate).isBetween(otherStartDate, otherEndDate))
  );
}

type FindGapsOptions = Partial<DayjsDateRange> & {
  allowConsecutiveDays?: boolean;
};

/**
 * Given an array of date ranges, find the dates not covered optionally starting from a given date
 * edge cases:
 * - no ranges given: returned range is from startDate to endDate
 * - no startDate given: returned range is from the first range to endDate
 * - no endDate: assumed to be today
 * - if no ranges are given and no startDate is given, return an empty array
 * @param ranges
 * @param options
 */
export function findGaps(ranges: DayjsDateRange[], options?: FindGapsOptions): DayjsDateRange[] {
  const endDate: Dayjs = options?.endDate ?? dayjs();
  if (!ranges.length) {
    if (!options?.startDate) {
      return [];
    }

    return [{ startDate: options.startDate, endDate }];
  }

  const sortedRanges: DayjsDateRange[] = cloneDeep(ranges).sort(
    (a: DayjsDateRange, b: DayjsDateRange) =>
      a.startDate.diff(b.startDate) || a.endDate.diff(b.endDate)
  );

  const gaps: DayjsDateRange[] = [];
  let latestPoint: Dayjs = options?.startDate ?? sortedRanges[0].startDate;

  sortedRanges.forEach((range: DayjsDateRange) => {
    if (range.startDate.isAfter(latestPoint) && range.startDate.isBefore(endDate)) {
      gaps.push({
        startDate: latestPoint,
        endDate: options?.allowConsecutiveDays
          ? range.startDate.subtract(1, 'day')
          : range.startDate
      });
    }

    if (range.endDate.isAfter(latestPoint)) {
      latestPoint = options?.allowConsecutiveDays ? range.endDate.add(1, 'day') : range.endDate;
    }
  });

  if (endDate.isAfter(latestPoint)) {
    gaps.push({ startDate: latestPoint, endDate });
  }

  return gaps;
}

export function yesterday(): Dayjs {
  return dayjs().subtract(1, 'day');
}

export function aYearFromNow(): Dayjs {
  return dayjs().add(1, 'year');
}
